import { isEmpty, pickBy } from "lodash";
import { monitors, me, MonitorActivityType } from "../fixtures";
import { startOfTomorrow } from "date-fns";

const wait = async (timeout = 350) => {
  await new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
};

export const deleteMonitor = async (monitorId: string) => {
  await wait();

  for (let i = 0; i < monitors.length; i++) {
    const monitor = monitors[i];

    if (monitor.id !== monitorId) {
      continue;
    }

    if (monitor._deleted) {
      continue;
    }

    monitors[i] = {
      ...monitor,
      _deleted: true,
      activity: [
        {
          id: `monitor.activity.${new Date().getMilliseconds()}`,
          date: new Date().toISOString(),
          type: MonitorActivityType.Unenrolled,
          unenrollingUserId: me.id,
        },
        ...monitor.activity,
      ],
    };
  }
};

export const unDeleteMonitor = async (monitorId: string) => {
  await wait();

  for (let i = 0; i < monitors.length; i++) {
    const monitor = monitors[i];

    if (monitor.id !== monitorId) {
      continue;
    }

    monitors[i] = {
      ...monitor,
      _deleted: false,
      activity: [
        {
          id: `monitor.activity.${new Date().getMilliseconds()}`,
          date: new Date().toISOString(),
          type: MonitorActivityType.Enrolled,
          enrollingUserId: me.id,
        },
        ...monitor.activity,
      ],
    };
  }
};

export interface CreateMonitorInput {
  domain: string;
  merchantName?: string | null | undefined;
  merchantDescription?: string | null | undefined;
  merchantMcc: string;
  externalReferenceId?: string | null | undefined;
  emailAddress?: string | null | undefined;
  phoneNumber?: string | null | undefined;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    stateProvince?: string;
    postalCode: string;
    country: string;
  };
}

export const createMonitor = async (monitorData: CreateMonitorInput) => {
  await wait();

  const address = pickBy(monitorData?.address || {});

  const cleaned = {
    id: `monitor.${Number(new Date())}`,
    ...pickBy(monitorData),
    ...(isEmpty(address) ? {} : { address }),
    activity: [
      {
        id: `monitor.activity.${new Date().getMilliseconds()}`,
        date: new Date().toISOString(),
        enrollingUserId: me.id,
        type: MonitorActivityType.Enrolled,
      },
    ],
    nextRun: startOfTomorrow().toISOString(),
  };

  monitors[monitors.length] = cleaned as any;
};

export interface EditMonitorInput {
  merchantName?: string | null | undefined;
  merchantDescription?: string | null | undefined;
  merchantMcc: string;
  externalReferenceId?: string | null | undefined;
  emailAddress?: string | null | undefined;
  phoneNumber?: string | null | undefined;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    stateProvince?: string;
    postalCode: string;
    country: string;
  };
}

export const editMonitor = async (
  monitorId: string,
  monitorData: EditMonitorInput,
) => {
  await wait();

  const monitorIdx = monitors.findIndex((m) => m.id === monitorId);

  if (monitorIdx < 0) {
    throw new Error(`Could not find monitor "${monitorId}"`);
  }

  const monitor = monitors[monitorIdx];

  const address = pickBy(monitorData?.address || {});

  const cleaned = {
    ...monitor,
    ...pickBy(monitorData),
    ...(isEmpty(address) ? {} : { address }),
    activity: [
      {
        id: `monitor.activity.${new Date().getMilliseconds()}`,
        date: new Date().toISOString(),
        type: MonitorActivityType.Edited,
        editingUserId: me.id,
      },
      ...monitor.activity,
    ],
  } as any;

  monitors[monitorIdx] = cleaned;
};
